import { useMedplum } from '@medplum/react';
import { useEffect, useState } from 'react';
import type { CarePlan, EpisodeOfCare, QuestionnaireResponse } from '@medplum/fhirtypes';
import { TEMPLATE_CATEGORY_SYSTEM } from '@unify/fhir-model/constants';
import {
  collectActiveTreatmentPlanDiagnoses,
  collectActiveTreatmentPlanDiagnosesFromCarePlans,
  mergeActiveTreatmentPlanDiagnosisFallback,
  type ActiveTreatmentPlanDiagnosis,
} from '../lib/chart/activeTreatmentPlanDiagnoses';

/**
 * Diagnoses from active treatment-plan CarePlans on each active/on-hold episode
 * (QR draft fallback when an episode has no CarePlan yet).
 */
export function useClientActiveTreatmentPlanDiagnoses(patientId: string | undefined): {
  diagnoses: ActiveTreatmentPlanDiagnosis[];
  loading: boolean;
} {
  const medplum = useMedplum();
  const [diagnoses, setDiagnoses] = useState<ActiveTreatmentPlanDiagnosis[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!patientId) {
      setDiagnoses([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    void (async () => {
      try {
        medplum.invalidateSearches('EpisodeOfCare');
        medplum.invalidateSearches('QuestionnaireResponse');
        medplum.invalidateSearches('CarePlan');

        const [eocBundle, qrBundle, carePlanBundle] = await Promise.all([
          medplum.search('EpisodeOfCare', {
            patient: `Patient/${patientId}`,
            status: 'active,onhold',
            _count: '50',
          }),
          medplum.search('QuestionnaireResponse', {
            subject: `Patient/${patientId}`,
            _tag: `${TEMPLATE_CATEGORY_SYSTEM}|treatment-planning`,
            _sort: '-authored',
            _count: '50',
          }),
          medplum.search('CarePlan', {
            subject: `Patient/${patientId}`,
            _tag: `${TEMPLATE_CATEGORY_SYSTEM}|treatment-planning`,
            _count: '50',
          }).catch(() => ({ entry: [] })),
        ]);

        const episodes = (eocBundle.entry ?? [])
          .map((e) => e.resource as EpisodeOfCare | undefined)
          .filter((e): e is EpisodeOfCare => Boolean(e));
        const responses = (qrBundle.entry ?? [])
          .map((e) => e.resource as QuestionnaireResponse | undefined)
          .filter((r): r is QuestionnaireResponse => Boolean(r));

        const nextFromPlans = collectActiveTreatmentPlanDiagnosesFromCarePlans({
          episodes,
          carePlans: (carePlanBundle.entry ?? [])
            .map((e) => e.resource as CarePlan | undefined)
            .filter((p): p is CarePlan => Boolean(p)),
        });
        const next = mergeActiveTreatmentPlanDiagnosisFallback(
          nextFromPlans,
          collectActiveTreatmentPlanDiagnoses({ episodes, responses }),
        );
        if (!cancelled) setDiagnoses(next);
      } catch {
        if (!cancelled) setDiagnoses([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [patientId, medplum]);

  return { diagnoses, loading };
}
